/**
 * Provides APIs to work on Followers.
 * These APIs are used to add/delete/get case followers.
 * @module CaseFollowerApi
 */
/*
 * Followers API$
 *
 * Copyright (c) 2021  Pegasystems Inc.
 * All rights reserved.
 *
 * This  software  has  been  provided pursuant  to  a  License
 * Agreement  containing  restrictions on  its  use.   The  software
 * contains  valuable  trade secrets and proprietary information  of
 * Pegasystems Inc and is protected by  federal   copyright law.  It
 * may  not be copied,  modified,  translated or distributed in  any
 * form or medium,  disclosed to third parties or used in any manner
 * not provided for in  said  License Agreement except with  written
 * authorization from Pegasystems Inc.
 */
import { invokeRestApi } from '../rest-client';

const ADD_CASE_FOLLOWERS = 'addCaseFollower';
const DELETE_CASE_FOLLOWER = 'deleteCaseFollower';
const GET_CASE_FOLLOWERS = 'getCaseFollowers';

const headers = { 'Content-Type': 'application/json' };

export type Followers = {
  name: string;
  ID: string;
  email: string;
};

export type FollowersResponse = {
  data: {
    followers: Followers[];
  };
};

/**
 * This API adds specified users as followers to a case.
 * @function
 * @static
 * @example <caption>In this example, the API adds a user whose ID is user-1 as follower to the case whose ID is W-102.</caption>
 * PCore.getCaseFollowerApi().addCaseFollower('ORG-APP-WORK W-102', ['user-1'], 'app/primary_1')
 * .then(() => {
 *   // success
 * }).catch(err => {
 *    // Error handling
 * });
 *
 * @param caseID     The ID of the case pointing to pzInsKey value to which the user need to be added as follower.
 * @param userID   The ID of the user which needs to be added added as follower to a case passed as an array. Eg: ["user-1","user-2"]
 * @param context    The name of the context from where the API is being called.
 * @returns A Promise that adds the specified users as followers to a case.
 */
export const addCaseFollower = (caseID: string, userID: string[], context: string): Promise<object> => {
  const queryPayload = {
    caseID
  };
  const reqBody = JSON.stringify({
    users: userID.map((user: string) => ({ ID: user }))
  });
  return invokeRestApi(ADD_CASE_FOLLOWERS, { body: reqBody, queryPayload, headers }, context);
};

/**
 * This API deletes the follower of a case.
 * @function
 * @static
 * @example <caption>In this example, he API deletes the follower whose ID is user-1 for case whose ID is W-02.</caption>
 * PCore.getCaseFollowerApi().deleteCaseFollower('ORG-APP-WORK W-02', 'user-1', 'app/primary_1')
 * .then(() => {
 *    // success
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param caseID     An ID of the case pointing to pzInsKey value for which we want to delete the follower
 * @param followerID An ID of the follower which we want to delete for a case
 * @param context    The name of the context from where the API is being called.
 */
export const deleteCaseFollower = (caseID: string, followerID: string, context: string) => {
  const queryPayload = {
    caseID,
    followerID
  };
  return invokeRestApi(DELETE_CASE_FOLLOWER, { queryPayload }, context);
};

/**
 * Returns a Promise that gets the followers of a case.
 * @function
 * @static
 * @example <caption>In this example, the API obtains the followers of a case whose ID is W-102.</caption>
 * PCore.getCaseFollowerApi().getCaseFollowers('ORG-APP-WORK W-102', 'app/primary_1')
 * .then(followers => {
 *    // followers array
 * }).catch(err => {
 *   // errors
 * });
 *
 * @param caseID     The ID of the case pointing to pzInsKey value whose followers must be obtained.
 * @param context    The name of the context from where the API is being called.
 * @returns A Promise that obtains the followers of a case.
 */
export const getCaseFollowers = (caseID: string, context: string): Promise<object> => {
  const queryPayload = { caseID };
  return invokeRestApi(GET_CASE_FOLLOWERS, { queryPayload }, context).then(
    (response: FollowersResponse) => response.data.followers
  );
};

export default {
  addCaseFollower,
  deleteCaseFollower,
  getCaseFollowers
};
